// IndexedDB mirror of the server (conversations, messages, media blobs, meta).
// The phone is a REFLECTION of the database: reads come from here first when
// offline; writes always go to the server (offlineQueue) and are mirrored back.
const DB_NAME = "osc_offline_v1";
const DB_VERSION = 1;
let dbPromise = null;

export class QuotaError extends Error { constructor() { super("quota"); this.name = "QuotaExceededError"; } }

function open() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") { reject(new Error("no_idb")); return; }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("conversations")) db.createObjectStore("conversations", { keyPath: "id" });
      if (!db.objectStoreNames.contains("messages")) {
        const s = db.createObjectStore("messages", { keyPath: "id" });
        s.createIndex("conv", "conversation_id", { unique: false });
      }
      if (!db.objectStoreNames.contains("media")) {
        const s = db.createObjectStore("media", { keyPath: "id" });
        s.createIndex("conv", "conversation_id", { unique: false });
      }
      if (!db.objectStoreNames.contains("meta")) db.createObjectStore("meta", { keyPath: "key" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

const isQuota = (err) => err && (err.name === "QuotaExceededError" || /quota/i.test(String(err.message || err.name || "")));

function tx(store, mode, fn) {
  return open().then((db) => new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    const s = t.objectStore(store);
    let out;
    try { out = fn(s); } catch (e) { reject(isQuota(e) ? new QuotaError() : e); return; }
    t.oncomplete = () => resolve(out && out.result !== undefined ? out.result : out);
    t.onerror = () => reject(isQuota(t.error) ? new QuotaError() : t.error);
    t.onabort = () => reject(isQuota(t.error) ? new QuotaError() : (t.error || new Error("abort")));
  }));
}

const reqAll = (req) => new Promise((resolve, reject) => { req.onsuccess = () => resolve(req.result); req.onerror = () => reject(req.error); });

export const offlineStore = {
  available: () => typeof indexedDB !== "undefined",
  // ---- conversations ----
  putConversations: (list) => tx("conversations", "readwrite", (s) => { list.forEach((c) => s.put(c)); }),
  getConversations: () => open().then((db) => reqAll(db.transaction("conversations").objectStore("conversations").getAll())),
  deleteConversation: (id) => tx("conversations", "readwrite", (s) => s.delete(id)),
  // ---- messages ----
  putMessages: (list) => tx("messages", "readwrite", (s) => { list.forEach((m) => s.put(m)); }),
  findMessage: (id) => open().then((db) => reqAll(db.transaction("messages").objectStore("messages").get(id))),
  getMessages: (convId) => open().then((db) => reqAll(db.transaction("messages").objectStore("messages").index("conv").getAll(convId)))
    .then((l) => l.sort((a, b) => (a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : 0))),
  deleteMessage: (id) => tx("messages", "readwrite", (s) => s.delete(id)),
  deleteMessagesOf: (convId) => open().then((db) => reqAll(db.transaction("messages").objectStore("messages").index("conv").getAllKeys(convId)))
    .then((keys) => tx("messages", "readwrite", (s) => { keys.forEach((k) => s.delete(k)); })),
  countMessages: () => open().then((db) => reqAll(db.transaction("messages").objectStore("messages").count())),
  // ---- media blobs ----
  putMedia: (rec) => tx("media", "readwrite", (s) => s.put(rec)), // {id, conversation_id, kind, quality: thumb|full, blob, size, created_at}
  getMedia: (id) => open().then((db) => reqAll(db.transaction("media").objectStore("media").get(id))),
  getAllMedia: () => open().then((db) => reqAll(db.transaction("media").objectStore("media").getAll())),
  getMediaKeys: () => open().then((db) => reqAll(db.transaction("media").objectStore("media").getAllKeys())),
  deleteMedia: (id) => tx("media", "readwrite", (s) => s.delete(id)),
  deleteMediaOf: (convId) => open().then((db) => reqAll(db.transaction("media").objectStore("media").index("conv").getAllKeys(convId)))
    .then((keys) => tx("media", "readwrite", (s) => { keys.forEach((k) => s.delete(k)); })),
  clearMedia: () => tx("media", "readwrite", (s) => s.clear()),
  // ---- meta (sync cursors, settings) ----
  getMeta: (key) => open().then((db) => reqAll(db.transaction("meta").objectStore("meta").get(key))).then((r) => (r ? r.value : undefined)),
  setMeta: (key, value) => tx("meta", "readwrite", (s) => s.put({ key, value })),
  clearAll: () => Promise.all(["conversations", "messages", "media", "meta"].map((st) => tx(st, "readwrite", (s) => s.clear()))),
  // usage summary for the settings / "storage full" screens
  usage: async () => {
    const media = await offlineStore.getAllMedia().catch(() => []);
    const bytes = media.reduce((s, m) => s + (m.size || (m.blob && m.blob.size) || 0), 0);
    const convs = await offlineStore.getConversations().catch(() => []);
    const msgs = await offlineStore.countMessages().catch(() => 0);
    let quota = null;
    try { if (navigator.storage?.estimate) quota = await navigator.storage.estimate(); } catch (e) { /* ignore */ }
    return { mediaCount: media.length, mediaBytes: bytes, conversations: convs.length, messages: msgs, quota };
  },
};
